import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Button, Loading, Typography, useSnackbar,
} from '@rrexpress/core';
import { useDocumentTitle } from '@refinedev/react-router-v6';

import BluebirdImage from '@/assets/images/bluebird.svg';
import { BaseForm } from '@/components/form/form';
import FormField from '@/components/form/form-field';
import { EyeCloseIcon, EyeIcon } from '@/components/icons';
import { useAccount } from '@/modules/auth/application/account';
import { RenderIf } from '@/components/conditional/render-if';
import { type IAccountActivationTokenCheckerPayload, type IAccountActivationTokenCheckerResponse, type IAccountPayload } from '@/modules/auth/model/account';

const AccountActivation = () => {
  const { token_code: tokenCode } = useParams<{ token_code: string }>();
  const { activateAccount, activationAccountTokenChecker, isLoading } = useAccount();
  const setTitle = useDocumentTitle();
  const { show } = useSnackbar();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<IAccountActivationTokenCheckerResponse>();
  const [isSuccess, setIsSuccess] = useState(false);

  async function handleOnSubmit(values: IAccountPayload) {
    if (values.password !== values.password_confirmation) {
      show('Password tidak sama', 'error');
      return;
    }

    try {
      await activateAccount({
        ...values,
        token_code: tokenCode as string,
      });
      setIsSuccess(true);
      show('Sukses aktivasi akun', 'success');
    } catch (error) {
      show('Gagal aktivasi akun', 'error');
    }
  }

  const footerButton = () => (
    <Button
      className="w-full"
      variant="primary"
    >
      Lanjutkan
    </Button>
  );

  async function hadleTokenChecker() {
    try {
      await activationAccountTokenChecker({
        token_code: tokenCode,
      } as IAccountActivationTokenCheckerPayload)
        .then((res) => setUserInfo(res?.data?.data));
    } catch (error) {
      // TODO
    }
  }

  useEffect(() => {
    setTitle('Account Activation | Bluebird Kirim');

    return () => {
      setTitle('Bluebird Kirim');
    };
  }, []);

  useEffect(() => {
    if (tokenCode) {
      hadleTokenChecker();
    }
  }, [tokenCode]);

  return (
    <div className="container mx-auto h-[100vh] flex flex-col items-center justify-center">
      <div className="p-8 text-black lg:min-w-[30vw]">
        {isLoading
          ? <Loading
            variant="circular"
            size={40}
            speed={0.6}
            indicatorCap="round"
          /> : <></>}
        <div className={`flex flex-col justify-center text-center ${isLoading && 'hidden'}`}>
          <img src={BluebirdImage} className="w-[144px] mx-auto mb-4" />
          <RenderIf
            condition={userInfo?.is_token_available}
            fallback={(
              <div className="mb-4">
                <Typography level={6} fontWeight="regular">Maaf, link aktivasi tidak valid.</Typography>
              </div>
            )}
          >
            <RenderIf
              condition={isSuccess}
              fallback={(
                <>
                  <div className="mb-4">
                    <Typography level={3}>Transport Management System</Typography>
                  </div>
                  <div className="mb-4">
                    <Typography level={4} fontWeight="medium">Aktivasi Akun</Typography>
                  </div>
                  <div className="my-2 text-grey">
                    <BaseForm
                      onFinish={handleOnSubmit}
                      footer={footerButton()}
                    >
                      <>
                        <div className="bg-[#F4F8FD] py-3 px-5 rounded-lg mb-4">
                          <div className="mb-4 flex gap-6 w-full text-left items-end">
                            <div className="w-[250px]">
                              <Typography level={6}>Nama Perusahaan</Typography>
                            </div>
                            <Typography level={6} fontWeight="medium">{userInfo?.company_name || '-'}</Typography>
                          </div>

                          <div className="mb-4 flex gap-6 w-full text-left items-end">
                            <div className="w-[250px]">
                              <Typography level={6}>Industri</Typography>
                            </div>
                            <Typography level={6} fontWeight="medium">{userInfo?.industry_name || '-'}</Typography>
                          </div>

                          <div className="mb-4 flex gap-6 w-full text-left items-end">
                            <div className="w-[250px]">
                              <Typography level={6}>Email Terdaftar</Typography>
                            </div>
                            <Typography level={6} fontWeight="medium">{userInfo?.email || '-'}</Typography>
                          </div>

                          <div className="mb-4 flex gap-6 w-full text-left items-end">
                            <div className="w-[250px]">
                              <Typography level={6}>Nama Depan</Typography>
                            </div>
                            <Typography level={6} fontWeight="medium">{userInfo?.first_name || '-'}</Typography>
                          </div>

                          <div className="mb-4 flex gap-6 w-full text-left items-end">
                            <div className="w-[250px]">
                              <Typography level={6}>Nama Belakang</Typography>
                            </div>
                            <Typography level={6} fontWeight="medium">{userInfo?.last_name || '-'}</Typography>
                          </div>
                        </div>

                        <div className="mb-4 flex gap-3 items-center justify-between">
                          <Typography level={5}>
                            <label htmlFor="password" className="font-medium">Password</label>
                          </Typography>
                          <div className="w-[350px]">
                            <FormField
                              component="form"
                              name="password"
                              componentProps={{
                                type: showPassword ? 'text' : 'password',
                                label: 'Password',
                                required: true,
                                rightIcon: (
                                  <div onClick={() => setShowPassword((password) => !password)} className="cursor-pointer">
                                    {showPassword ? (
                                      <EyeIcon />
                                    ) : (
                                      <EyeCloseIcon />
                                    )}
                                  </div>
                                ),
                              }}
                            />
                          </div>
                        </div>
                        <div className="mb-4 flex gap-3 items-center justify-between">
                          <Typography level={5}>
                            <label htmlFor="password_confirmation" className="font-medium">Confirm Password</label>
                          </Typography>
                          <div className="w-[350px]">
                            <FormField
                              component="form"
                              name="password_confirmation"
                              componentProps={{
                                type: showConfirmPassword ? 'text' : 'password',
                                label: 'Konfirmasi Password',
                                required: true,
                                rightIcon: (
                                  <div onClick={() => setShowConfirmPassword((password) => !password)} className="cursor-pointer">
                                    {showConfirmPassword ? (
                                      <EyeIcon />
                                    ) : (
                                      <EyeCloseIcon />
                                    )}
                                  </div>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      </>
                    </BaseForm>
                  </div>
                </>
              )}
            >
              <div className="mb-4">
                <Typography level={6} fontWeight="regular">Sukses aktivasi akun, silahkan login.</Typography>
              </div>
              <Link to="/auth/login">
                <Button
                  className="w-full"
                  variant="primary"
                >
                  Login
                </Button>
              </Link>
            </RenderIf>
          </RenderIf>
        </div>
      </div>
    </div>
  );
};

export default AccountActivation;
